import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  output,
  signal,
  OnInit,
  OnDestroy,
  computed,
} from '@angular/core';
import {
  ControlContainer,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { Subscription } from 'rxjs';
import { TrimDirective } from '../../../ui-components/directives/trim/trim.directive';
import { ValidationMessageComponent } from '../../../ui-components/lib/validation-message/validation-message.component';
import { FormBuilderService } from '../../../ui-components/services/form-builder/form-builder.service';

@Component({
  selector: 'lib-dual-lang-text-area',
  imports: [
    ReactiveFormsModule,
    TrimDirective,
    ValidationMessageComponent,
    TranslocoPipe,
  ],
  templateUrl: './dual-lang-text-area.component.html',
  styleUrl: './dual-lang-text-area.component.scss',
  viewProviders: [
    {
      provide: ControlContainer,
      useFactory: () => inject(ControlContainer, { skipSelf: true }),
    },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class DualLangTextAreaComponent implements OnInit, OnDestroy {
  private readonly formBuilderService = inject(FormBuilderService);
  private readonly transloco = inject(TranslocoService);

  inputFormControlName = input.required<string>();
  id = input.required<string>();
  name = input.required<string>();

  label = input<string>();
  placeholderEn = input<string>('');
  placeholderAr = input<string>('');
  inputClass = input<string>('');
  width = input<string>('42.3125');
  height = input<string>('6.5');
  isHasToolTip = input<boolean>(false);
  canResize = input<boolean>(true);

  startIcon = input<string>();
  endIcon = input<string>();

  minlength = input<number>();
  maxlength = input<number>();
  rowsNumber = input<string>('4');

  disabled = input<boolean>(false);
  readonly = input<boolean>();
  value = input<string | number>();
  requiredEn = input<boolean>(true);
  requiredAr = input<boolean>(true);
  isSingleInput = input<boolean>(false);

  endIconCLick = output<void>();
  typing = output<string>();

  displayPlaceholderEn = computed(() => {
    const providedPlaceholder = this.placeholderEn();
    if (providedPlaceholder) {
      return providedPlaceholder;
    }
    return this.transloco.translate(
      'dual_lang_textarea_default_en_placeholder'
    );
  });

  displayPlaceholderAr = computed(() => {
    const providedPlaceholder = this.placeholderAr();
    if (providedPlaceholder) {
      return providedPlaceholder;
    }
    return this.transloco.translate(
      'dual_lang_textarea_default_ar_placeholder'
    );
  });

  currentLang = signal<'EN' | 'AR'>('EN');

  requiredMessageEn = signal<string>('');
  requiredMessageAr = signal<string>('');
  equation = input<string | undefined>(undefined);
  subscriptions = new Subscription();

  private readonly parentContainer = inject(ControlContainer);
  private readonly translocoService = inject(TranslocoService);

  private get parentFormGroup() {
    return this.parentContainer.control as FormGroup;
  }

  get childControlEn() {
    const controlName = this.isSingleInput()
      ? this.inputFormControlName()
      : `en`;
    return this.parentFormGroup?.controls[controlName] as FormControl;
  }

  get childControlAr() {
    const controlName = `ar`;
    return this.parentFormGroup?.controls[controlName] as FormControl;
  }

  ngOnInit(): void {
    if (this.disabled()) {
      this.childControlEn?.disable();
      this.childControlAr?.disable();
    }

    if (this.requiredEn() && this.childControlEn) {
      this.childControlEn.addValidators(Validators.required);
      this.childControlEn.updateValueAndValidity();
    }

    if (this.requiredAr() && this.childControlAr) {
      this.childControlAr.addValidators(Validators.required);
      this.childControlAr.updateValueAndValidity();
    }

    this.translocoService.langChanges$.subscribe(lang => {
      if (lang === 'en') {
        this.currentLang.set('EN');
      } else {
        this.currentLang.set('AR');
      }
    });

    this.updateLanguageBasedOnDirection();

    this.handleEquation();
  }

  updateLanguageBasedOnDirection() {
    const isRTL = document.documentElement.dir === 'rtl';

    if (isRTL) {
      this.currentLang.set('AR');
      this.requiredMessageEn.set('اللغة الإنجليزية مطلوبة');
      this.requiredMessageAr.set('اللغة العربية مطلوبة');
    } else {
      this.requiredMessageEn.set('English language is required.');
      this.requiredMessageAr.set('Arabic language is required.');
      this.currentLang.set('EN');
    }
  }

  applyActionOnEndIconClicked() {
    this.endIconCLick.emit();
  }

  switchLanguage(lang: 'EN' | 'AR') {
    this.currentLang.set(lang);
  }

  handleEquation() {
    if (this.equation()) {
      const grandParentFormGroup = this.parentFormGroup.parent as FormGroup;

      const dynamicEquationParams = [
        this.equation() as string,
        this.inputFormControlName(),
        grandParentFormGroup,
      ];

      // handle single equation ONLY
      if (!this.equation()?.includes(' ')) {
        const equationControls =
          this.formBuilderService.addDualLangEquationFormControls(
            dynamicEquationParams
          );
        const equationSubEn = equationControls.en.valueChanges.subscribe(
          value => {
            this.childControlEn?.setValue(value);
          }
        );
        const equationSubAr = equationControls.ar.valueChanges.subscribe(
          value => {
            this.childControlAr?.setValue(value);
          }
        );

        this.subscriptions.add(equationSubEn);
        this.subscriptions.add(equationSubAr);
      }
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
